import { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';

import { Theme } from '@app/theme';
import { CenteredButton, Link, useToast } from '@app/core';
import { Trans, useTranslation } from 'react-i18next';
import { RulesIndexBox } from './components/RulesIndexBox';

import search from '@app/illu/Small/Search.svg';
import thumbsUp from '@app/illu/Small/ThumbsUp.svg';
import survey from '@app/illu/Small/Survey.svg';
import wrong from '@app/illu/Small/Wrong.svg';
import {
  Consent,
  ConsentName,
  useAcceptConsent,
  useGetConsentConfig,
} from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 7,
  },
  title: {
    ...theme.fonts.h3,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 4,
  },
  moreInfo: {
    ...theme.fonts.caption,
  },
  link: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
}));

interface Props {
  onSubmit: () => any;
  hasIncentive?: boolean;
}

const bold = { b: <b /> };

export const ParticipationRules = ({ onSubmit, hasIncentive }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();
  const getConsentConfig = useGetConsentConfig();
  const consentConfigParticipantRules = useMemo(
    () => getConsentConfig(ConsentName.PARTICIPANT_RULES),
    [getConsentConfig],
  );

  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: acceptConsent } = useAcceptConsent();
  const currentConsent: Consent = useMemo(
    () => ({
      documentId: consentConfigParticipantRules.id,
      date: new Date(),
    }),
    [consentConfigParticipantRules],
  );

  const submit = useCallback(async () => {
    try {
      setIsSaving(true);
      await Promise.all([acceptConsent(currentConsent), onSubmit()]);
    } catch (e) {
      toast.exception(e);
    }
    setIsSaving(false);
  }, [toast, acceptConsent, currentConsent, onSubmit]);

  return (
    <div className={classes.container}>
      <h1 className={classes.title}>{t('participationRules.content.title')}</h1>
      <div className={classes.wrapper}>
        <RulesIndexBox
          img={search}
          text={
            <Trans
              i18nKey="participationRules.content.search"
              components={bold}
            />
          }
        />
        <RulesIndexBox
          img={thumbsUp}
          text={
            <Trans
              i18nKey="participationRules.content.thumbsUp"
              components={bold}
            />
          }
        />
        {hasIncentive && (
          <RulesIndexBox
            img={survey}
            text={
              <Trans
                i18nKey="participationRules.content.survey"
                components={bold}
              />
            }
          />
        )}
        <RulesIndexBox
          img={wrong}
          text={
            hasIncentive ? (
              <Trans
                i18nKey="participationRules.content.wrong"
                components={bold}
              />
            ) : (
              <Trans
                i18nKey="participationRules.content.wrongNoIncentive"
                components={bold}
              />
            )
          }
        />
        <p className={classes.moreInfo}>
          <Trans
            i18nKey="participationRules.content.moreInfo"
            components={{
              a: (
                <Link
                  className={classes.link}
                  isExternalLink
                  href={consentConfigParticipantRules.url}
                  target="_blank"
                />
              ),
            }}
          />
        </p>
      </div>
      <CenteredButton
        onClick={submit}
        text={t('participationRules.submit')}
        fullMobileWidth
        withNextArrow
        isLoading={isSaving}
      />
    </div>
  );
};
